import React, { useEffect, useRef, useState } from "react";
import Shimmer from "../Common/Shimmer";
import { Callout } from "../ui";

/**
 * ZoomMeetingEmbedMobile — the Zoom meeting as rendered for a PHONE browser.
 *
 * Why this exists alongside ZoomMeetingEmbed.jsx rather than being a prop on
 * it: they are two different Zoom SDKs.
 *
 *  • ZoomMeetingEmbed uses **Client View** (`ZoomMtg`), which takes over the
 *    page — it appends `#zmmtg-root` to `document.body`, hides the app behind
 *    it, and lays itself out for a desktop window. That is the right call for
 *    a laptop and the wrong one for a 390px viewport.
 *  • This uses **Component View** (`@zoom/meetingsdk/embedded`), which renders
 *    into a div we own and is the view Zoom supports in mobile browsers. It
 *    can be sized to the viewport, it does not fight the page, and tearing it
 *    down is `leaveMeeting` rather than hunting stray modal overlays.
 *
 * The backend is unchanged: both take the same signature / sdkKey /
 * meetingNumber / passcode / userName that getBatchJoinConfig already returns.
 *
 * Props mirror the other embeds in this folder so MeetingProviderRouter can
 * treat all of them the same.
 */
const ZoomMeetingEmbedMobile = ({ zoomConfig, onMeetingEnd }) => {
  const containerRef = useRef(null);
  const clientRef = useRef(null);
  const isMountedRef = useRef(true);

  const [status, setStatus] = useState("loading"); // loading | in-meeting | error
  const [errorMsg, setErrorMsg] = useState("");

  // The SDK can re-emit "ended" (host ended + our own leave), and the parent
  // navigates away on the first one — so it fires exactly once.
  const endedRef = useRef(false);
  const onMeetingEndRef = useRef(onMeetingEnd);
  onMeetingEndRef.current = onMeetingEnd;
  const fireMeetingEnded = () => {
    if (endedRef.current) return;
    endedRef.current = true;
    if (onMeetingEndRef.current) onMeetingEndRef.current();
  };

  useEffect(() => {
    isMountedRef.current = true;

    const join = async () => {
      if (
        !zoomConfig?.meetingId ||
        !zoomConfig?.signature ||
        !zoomConfig?.sdkKey ||
        !zoomConfig?.userName
      ) {
        setErrorMsg("Class configuration is incomplete. Press Join in the app again.");
        setStatus("error");
        return;
      }

      try {
        const { default: ZoomMtgEmbedded } = await import("@zoom/meetingsdk/embedded");
        if (!isMountedRef.current) return;

        const client = ZoomMtgEmbedded.createClient();
        clientRef.current = client;

        await client.init({
          zoomAppRoot: containerRef.current,
          language: "en-US",
          // Zoom's own media-layer patches. Without this the SDK breaks on
          // several mobile browsers exactly where it is hardest to debug.
          patchJsMedia: true,
        });
        if (!isMountedRef.current) return;

        await client.join({
          signature: zoomConfig.signature,
          sdkKey: zoomConfig.sdkKey,
          meetingNumber: String(zoomConfig.meetingId),
          password: zoomConfig.passcode || "",
          userName: zoomConfig.userName,
          userEmail: zoomConfig.userEmail || "",
          tk: zoomConfig.registrantToken || "",
          zak: zoomConfig.zakToken || "",
        });
        if (!isMountedRef.current) return;

        setStatus("in-meeting");

        // 'Closed' is the meeting being over (the host ended it, or this
        // person left); 'Fail' is a connection that cannot be recovered. Both
        // mean the class is finished for whoever is looking at this page.
        client.on("connection-change", (payload) => {
          if (payload?.state === "Closed" || payload?.state === "Fail") fireMeetingEnded();
        });
      } catch (err) {
        if (!isMountedRef.current) return;
        console.error("[ZoomSDK/mobile] join failed:", err);
        setErrorMsg(
          err?.reason || err?.message || "We couldn't start the class in this browser."
        );
        setStatus("error");
      }
    };

    join();

    return () => {
      isMountedRef.current = false;
      // This is what releases the camera and microphone. Skipping it leaves the
      // phone's camera light on after the student has closed the class.
      try {
        clientRef.current?.leaveMeeting();
      } catch {
        /* never joined, or already gone */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Parent gates rendering until the config is ready.

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
      {status === "loading" && (
        <div className="tw-p-4">
          <Shimmer height={320} />
          <p className="tw-mt-3 tw-text-center tw-text-sm tw-text-content-muted">
            Starting your class…
          </p>
        </div>
      )}

      {status === "error" && (
        <div className="tw-p-4">
          <Callout tone="danger" role="alert">
            {errorMsg}
          </Callout>
        </div>
      )}

      {/*
        The SDK mounts here. It must stay in the tree while loading — Zoom
        measures this element during init, so rendering it only once we are
        "in-meeting" would hand it a node with no size.
      */}
      <div
        ref={containerRef}
        className="tw-min-h-0 tw-flex-1"
        style={{ display: status === "error" ? "none" : "block" }}
      />
    </div>
  );
};

export default ZoomMeetingEmbedMobile;
